import { translate } from "app/filters/i18nFilter";
import type { ColummListColumn } from "app/pages/sales-intelligence/common-components/search-results-table";
import { tryTranslate } from "app/pages/sales-intelligence/utils/common";
import { columnKeys } from "app/pages/sales-intelligence/utils/i18n/columns";

import type { LeadsSearchRow, TableColumnTypeDto } from "../../types";
import type { ColumnAdapterContext } from "../../types/columns";
import { wrapColumnWithDetailsByCountryTooltip } from "../../utils/column-wrapper";

import type { ColumnConfig, DetailedColumnConfig, ITableColumnAdapters, TableColumnAdapter } from "./types";

const modifyBy = (ctx?: ColumnAdapterContext) => ctx?.leadsType;

const resolveDefaultTooltip = (name: string, ctx?: ColumnAdapterContext): string => {
    const modifier = modifyBy(ctx);

    let text: ReturnType<typeof tryTranslate>;
    if (modifier) {
        text = tryTranslate(columnKeys.tooltip(name, modifier));
    }

    return text ?? translate(columnKeys.tooltip(name));
};

export const tableColumnAdapters = {
    createTableColumnAdapter:
        <RecordType>(column: ColumnConfig<RecordType>): TableColumnAdapter<RecordType> =>
        (dto: TableColumnTypeDto, ctx?: ColumnAdapterContext): Readonly<ColummListColumn<RecordType>> =>
            ({
                title: translate(columnKeys.title(dto.name)),
                tooltipTitle: resolveDefaultTooltip(dto.name, ctx),
                fixed: column.fixed ?? (dto.isStatic ? "left" : false),
                width: 190,
                sorter: true,
                ellipsis: true,
                ...column,
            }) as Readonly<ColummListColumn<RecordType>>,

    /**
     * Creates table column adapter for dynamic columns (i.e tech).
     * Uses DTO name directly (backend-provided).
     */
    createDynamicTableColumnAdapter:
        <RecordType>(column: Partial<ColummListColumn<RecordType>>): TableColumnAdapter<RecordType> =>
        (dto: TableColumnTypeDto): Readonly<ColummListColumn<RecordType>> =>
            ({
                dataIndex: dto.id as keyof RecordType,
                title: dto.name,
                columnPickerTitle: dto.columnPickerName,
                additionalLabel: dto.additionalLabel,
                tooltipTitle: translate(columnKeys.tooltip(dto.type)),
                fixed: dto.isStatic ? "left" : false,
                sorter: true,
                ellipsis: true,
                ...column,
            }) as Readonly<ColummListColumn<RecordType>>,

    /**
     * Creates table column adapter for signal columns.
     */
    // TODO refactor SI-6770
    createSignalTableColumnAdapter:
        <RecordType>(column: Partial<ColummListColumn<RecordType>>): TableColumnAdapter<RecordType> =>
        (dto: TableColumnTypeDto): Readonly<ColummListColumn<RecordType>> =>
            ({
                dataIndex: dto.id as keyof RecordType,
                title: dto.name,
                tooltipTitle: dto.tooltipTitle,
                fixed: false,
                sorter: false,
                ellipsis: true,
                ...column,
            }) as Readonly<ColummListColumn<RecordType>>,

    /**
     * Creates table column adapter with detail tooltips.
     */
    createDetailedTableColumnAdapter: <RecordType extends LeadsSearchRow>(
        config: DetailedColumnConfig<RecordType>,
    ): TableColumnAdapter<RecordType> => {
        const { render, ...column } = config;

        return tableColumnAdapters.createTableColumnAdapter<RecordType>({
            render: wrapColumnWithDetailsByCountryTooltip<RecordType>(config.dataIndex, render),
            ...column,
        } as unknown as ColumnConfig<RecordType>);
    },
} satisfies ITableColumnAdapters;
